// SUB-OFFICE 7 — the articulated petitioner figure. Head, torso, upper/lower
// arms and legs assembled from box primitives: the citizens of the Bureau are
// photocopies of a person, not a person. One rig definition serves the player
// (real meshes) and the ghosts (one InstancedMesh per part, posed through a
// single scratch rig). Procedural walk cycle: leg swing, arm counter-swing,
// knee/elbow bend, torso lean, idle sway.
//
// v3-3 (§5.1 Path A): the geometry builders take an OPTIONAL per-part size
// scale so the PLAYER can carry a mild toy bulk on its primary masses (head +
// torso) and pop from the crowd. The default is no scale — ghosts pass nothing
// and their geometry is byte-identical to before. The brightness delta is a
// material choice made by the consumer (player.ts), not baked here; this file
// stays proportion-only and staff-shaped by default.
//
// Consumers: entities/player.ts (attachFigureMeshes) and entities/ghosts.ts
// (figureGeometries + FigureRig.pose + world matrices per part).

import { BoxGeometry, Group, Mesh, Object3D, type Material } from 'three';

export const FIGURE_PART_NAMES = [
  'head', 'torso',
  'armUL', 'armLL', 'armUR', 'armLR',
  'legUL', 'legLL', 'legUR', 'legLR',
] as const;
export type FigurePart = (typeof FIGURE_PART_NAMES)[number];

const HIP_Y = 0.92;
const LEG_U = 0.4; // hip -> knee
const LEG_L = 0.44; // knee -> ground (foot folded in)
const ARM_U = 0.3;
const ARM_L = 0.3;

interface PartDef {
  size: [number, number, number];
  /** geometry translate so the pivot sits at the joint */
  pivot: [number, number, number];
}

/** Box dimensions per part; pivots at the joint so rotation reads as a limb. */
const PART_DEFS: Record<FigurePart, PartDef> = {
  head: { size: [0.22, 0.24, 0.22], pivot: [0, 0.12, 0] },
  torso: { size: [0.38, 0.54, 0.22], pivot: [0, 0.27, 0] },
  armUL: { size: [0.09, LEG_U * 0 + ARM_U, 0.1], pivot: [0, -ARM_U / 2, 0] },
  armLL: { size: [0.08, ARM_L, 0.09], pivot: [0, -ARM_L / 2, 0] },
  armUR: { size: [0.09, ARM_U, 0.1], pivot: [0, -ARM_U / 2, 0] },
  armLR: { size: [0.08, ARM_L, 0.09], pivot: [0, -ARM_L / 2, 0] },
  legUL: { size: [0.12, LEG_U, 0.13], pivot: [0, -LEG_U / 2, 0] },
  legLL: { size: [0.11, LEG_L, 0.12], pivot: [0, -LEG_L / 2, 0] },
  legUR: { size: [0.12, LEG_U, 0.13], pivot: [0, -LEG_U / 2, 0] },
  legLR: { size: [0.11, LEG_L, 0.12], pivot: [0, -LEG_L / 2, 0] },
};

/**
 * Optional per-part size multiplier (x,y,z). Omitted parts stay at 1× (the
 * default staff proportion). The PLAYER passes a mild bulk on head + torso
 * (§5.1 Path A) to read as a chunky toy; the ghosts pass nothing, so their
 * geometry is byte-identical to pre-v3-3. Pivots scale with size so a taller
 * box keeps its joint at the same node.
 */
export type FigureScale = Partial<Record<FigurePart, readonly [number, number, number]>>;

/** One translated BoxGeometry per part (shared across all instances). With no
 *  `scale` the result is identical to the un-parameterized build (ghosts). */
export function figureGeometries(scale?: FigureScale): Record<FigurePart, BoxGeometry> {
  const out = {} as Record<FigurePart, BoxGeometry>;
  for (const name of FIGURE_PART_NAMES) {
    const d = PART_DEFS[name];
    const s = scale?.[name];
    const sx = s ? s[0] : 1, sy = s ? s[1] : 1, sz = s ? s[2] : 1;
    const g = new BoxGeometry(d.size[0] * sx, d.size[1] * sy, d.size[2] * sz);
    g.translate(d.pivot[0] * sx, d.pivot[1] * sy, d.pivot[2] * sz);
    out[name] = g;
  }
  return out;
}

export interface FigurePose {
  /** walk phase in radians (advances with distance walked) */
  phase: number;
  /** 0 standing .. 1 full stride */
  speed01: number;
  /** running clock for idle sway (any monotonically increasing seconds) */
  idleT: number;
}

/**
 * The scratch skeleton: transform nodes in a proper hierarchy. Pose it, call
 * root.updateMatrixWorld(true), then read nodes[part].matrixWorld — or attach
 * real meshes to the nodes for a non-instanced figure (the player).
 */
export class FigureRig {
  readonly root = new Group();
  readonly nodes: Record<FigurePart, Object3D>;

  constructor() {
    const n = {} as Record<FigurePart, Object3D>;
    for (const name of FIGURE_PART_NAMES) n[name] = new Object3D();

    // torso pivots at the hip; head rides its top
    n.torso.position.set(0, HIP_Y, 0);
    this.root.add(n.torso);
    n.head.position.set(0, 0.6, 0);
    n.torso.add(n.head);
    // shoulders
    n.armUL.position.set(-0.245, 0.48, 0);
    n.armUR.position.set(0.245, 0.48, 0);
    n.torso.add(n.armUL, n.armUR);
    n.armLL.position.set(0, -ARM_U, 0);
    n.armLR.position.set(0, -ARM_U, 0);
    n.armUL.add(n.armLL);
    n.armUR.add(n.armLR);
    // hips
    n.legUL.position.set(-0.11, HIP_Y, 0);
    n.legUR.position.set(0.11, HIP_Y, 0);
    this.root.add(n.legUL, n.legUR);
    n.legLL.position.set(0, -LEG_U, 0);
    n.legLR.position.set(0, -LEG_U, 0);
    n.legUL.add(n.legLL);
    n.legUR.add(n.legLR);

    this.nodes = n;
  }

  /** Apply the procedural walk/idle pose. Mechanisms ease; bodies stride. */
  pose(p: FigurePose): void {
    const n = this.nodes;
    const s = p.speed01;
    const swing = Math.sin(p.phase);
    const swingB = Math.sin(p.phase + Math.PI);

    // legs: swing about the hip; the trailing knee folds
    n.legUL.rotation.x = swing * 0.55 * s;
    n.legUR.rotation.x = swingB * 0.55 * s;
    n.legLL.rotation.x = Math.max(0, Math.sin(p.phase + 2.2)) * 0.75 * s;
    n.legLR.rotation.x = Math.max(0, Math.sin(p.phase + Math.PI + 2.2)) * 0.75 * s;

    // arms: counter-swing, elbows slightly bent, tightening with stride
    const dangle = Math.sin(p.idleT * 1.1) * 0.04;
    n.armUL.rotation.x = swingB * 0.45 * s + dangle;
    n.armUR.rotation.x = swing * 0.45 * s - dangle;
    n.armUL.rotation.z = 0.06;
    n.armUR.rotation.z = -0.06;
    n.armLL.rotation.x = -0.25 - Math.max(0, swingB) * 0.35 * s;
    n.armLR.rotation.x = -0.25 - Math.max(0, swing) * 0.35 * s;

    // torso: lean into the walk; idle sway when standing
    n.torso.rotation.x = 0.08 * s;
    n.torso.rotation.z = Math.sin(p.idleT * 0.8) * 0.02 * (1 - s);
    n.torso.rotation.y = swing * 0.06 * s;
    n.head.rotation.x = -0.06 * s; // the head stays level-ish against the lean
    n.torso.position.y = HIP_Y + Math.abs(Math.sin(p.phase)) * 0.028 * s;
  }
}

/**
 * Attach real meshes (the player): one Mesh per part on the rig's nodes,
 * all sharing the caller's material. Returns the meshes for visibility work.
 * `scale` fattens named parts (player bulk); omitted → default staff rig.
 */
export function attachFigureMeshes(
  rig: FigureRig, material: Material, scale?: FigureScale,
): Record<FigurePart, Mesh> {
  const geos = figureGeometries(scale);
  const out = {} as Record<FigurePart, Mesh>;
  for (const name of FIGURE_PART_NAMES) {
    const m = new Mesh(geos[name], material);
    rig.nodes[name].add(m);
    out[name] = m;
  }
  return out;
}
